import { all, put, call, getContext, takeLatest, select } from 'redux-saga/effects';
import format from 'date-fns/format';
import { Logger } from 'utils/Logger';
import { requestAllClients } from 'reducers/monitor/reducer';
import { InsightsGenerated, SnapshotGenerated } from 'enums/sse';
import {
  snapshot,
  createSnapshot,
  snapshotAccounts,
  deleteCreditLines,
  updateSnapshotAccount,
  updateSnapshotAccounts,
  archiveClient,
  deleteClient,
} from './types';
import { SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS } from '../connect-wizzard/types';
import {
  requestSnapshot,
  requestSnapshotSuccess,
  requestSnapshotFailure,
  requestSnapshotAccountsSuccess,
  requestSnapshotAccountsFailure,
  requestUpdateSnapshotAccountSuccess,
  requestUpdateSnapshotAccountFailure,
  requestUpdateSnapshotAccountsSuccess,
  requestUpdateSnapshotAccountsFailure,
  requestDeleteCreditLineSuccess,
  requestDeleteCreditLineFailure,
  setSnapshotCreating,
} from './reducer';

import { requestUpdateClientPreview, setClientGeneratedAt } from '../overview/actions';
import { resetIntegrationFilePayload } from '../connect-wizzard/actions';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

const logger = new Logger('Saga:Monitor');

export function* requestSnapshotSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.monitor.snapshot', { entityId: action.entityId.toString() }),
    );

    if (response.data && response.data.id) {
      yield put(requestSnapshotSuccess(response.data));

      if (action.updateClientPreview) {
        yield put(requestUpdateClientPreview(response.data?.entityId, response.data.preview));
      }
      action.promise && action.promise.resolve(response.data);
    } else {
      yield put(requestSnapshotFailure(response));
      action.promise && action.promise.reject(response);
    }
  } catch (error) {
    logger.error('Failed to request snapshot with the following error', error);
    yield put(requestSnapshotFailure(error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestSnapshotUpdateSaga(action) {
  try {
    const entityId = action?.payload?.entityId;
    const finished = action?.payload?.finished;
    const message = action?.payload?.message;
    const percentage = action?.payload?.percentage;
    const error = action?.payload?.error;
    const state = yield select();
    const knownEntity = state.toJS()?.monitor?.overview?.clients?.ids.includes(+entityId);
    const archivedView = state.toJS()?.monitor?.overview?.archivedView;
    if (
      !archivedView &&
      !knownEntity &&
      (finished || percentage === 100) &&
      message === SnapshotGenerated
    ) {
      const companyId = state.toJS()?.me?.user?.company?.company_id;
      yield put(requestAllClients(companyId));
    }
    if (!finished && percentage === 25) {
      yield put(
        setClientGeneratedAt({
          entityId,
          date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        }),
      );
    }
    if (error) {
      yield put(setSnapshotCreating(false));
      logger.error('Failed to generate snapshot', error);
      return;
    }
    yield put(setSnapshotCreating(!finished));
    if ((finished || percentage === 100) && message === SnapshotGenerated) {
      Analytics.track(AnalyticsEventCategory.monitor, AnalyticsEventAction.connectCompleted, {
        extraParams: {
          accountingProvider: action?.payload?.provider,
        },
      });
    }
    if (!finished || message !== InsightsGenerated) {
      return;
    }
    yield put(
      requestSnapshot(entityId, {
        resolve: () => null,
        reject: () => null,
      }),
    );
  } catch (error) {
    logger.error('Failed to request snapshot with the following error', error);
  }
}

export function* requestSnapshotAccountsSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const options = { entityId: action.entityId };

    if (action.viewedOnMonitor !== undefined) {
      Object.assign(options, { viewedOnMonitor: action.viewedOnMonitor });
    }

    const response = yield call(
      authApiService.get,
      routerService.generate('api.monitor.monitor_snapshot_accounts', options),
    );

    yield put(requestSnapshotAccountsSuccess(action.entityId, response.data));
  } catch (error) {
    logger.error('Failed to request snapshot accounts with the following error', error);
    yield put(requestSnapshotAccountsFailure(action.entityId, error));
  }
}

export function* requestUpdateSnapshotAccountsSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.monitor.monitor_snapshot_accounts', {
        entityId: action.entityId,
      }),
      action.payload,
    );

    yield put(requestUpdateSnapshotAccountsSuccess(action.entityId, response.data));
    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error(
      `Failed to request snapshot accounts update for ${action.entityId} with the following error`,
      error,
    );
    yield put(requestUpdateSnapshotAccountsFailure(action.entityId, error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestUpdateSnapshotAccountSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.monitor.mark_monitor_snapshot_accounts_as_viewed', {
        accountId: action.accountId,
        entityId: action.entityId,
      }),
      action.payload,
    );

    yield put(
      requestUpdateSnapshotAccountSuccess(action.entityId, action.accountId, response.data),
    );
    action.promise && action.promise.resolve(response.data);
  } catch (error) {
    logger.error(
      `Failed to update snapshot account ${action.accountId} with the following error`,
      error,
    );
    yield put(requestUpdateSnapshotAccountFailure(action.accountId, error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestDeleteCreditLineSaga(action) {
  const { entityId, creditLineId } = action;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.delete,
      routerService.generate('api.monitor.delete_credit_line_facility', { entityId, creditLineId }),
    );

    logger.info('Delete credit line');
    yield put(requestDeleteCreditLineSuccess(entityId, creditLineId));
    action.promise && action.promise.resolve();
  } catch (error) {
    logger.error(`Failed to delete credit line ${creditLineId} with the following error`, error);
    yield put(requestDeleteCreditLineFailure(error));
    action.promise && action.promise.reject(error);
  }
}

export function* triggerSnapshotCreation(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.post,
      routerService.generate('api.monitor.create_snapshot', {
        entityId: action.entityId.toString(),
      }),
      action.xeroPayload ? action.xeroPayload : {},
    );

    if (action.blockRefresh) {
      action.promise && action.promise.resolve(response.data);
      return;
    }

    yield put(requestSnapshotSuccess(response.data));
    action.promise && action.promise.resolve(response.data);
    if (action.xeroPayload) {
      yield put(resetIntegrationFilePayload());
    }
  } catch (error) {
    logger.error('Failed to create snapshot with the following error', error);
    action.promise && action.promise.reject(error);
    if (action.xeroPayload) {
      yield put(resetIntegrationFilePayload());
    }
  }
}

export function* requestArchiveClientSaga(action) {
  const { entityId, userCompanyId } = action;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.patch,
      routerService.generate('api.monitor.client_archive', { entityId }),
    );

    logger.info('Archived client');
    yield put(requestAllClients(userCompanyId));
  } catch (error) {
    logger.error('Failed to archive client with the following error', error);
  }
}

export function* requestDeleteClientSaga(action) {
  const { entityId, userCompanyId } = action;
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    yield call(
      authApiService.delete,
      routerService.generate('api.platform.entity_all', { entityId }),
    );

    logger.info('Deleted client');
    yield put(requestAllClients(userCompanyId));
  } catch (error) {
    logger.error('Failed to archive client with the following error', error);
  }
}

export default function* clientSaga() {
  return yield all([
    takeLatest(createSnapshot.request, triggerSnapshotCreation),
    takeLatest(snapshot.request, requestSnapshotSaga),
    takeLatest(SSE_UPDATE_SNAPSHOT_GENERATION_PROGRESS, requestSnapshotUpdateSaga),
    takeLatest(snapshotAccounts.request, requestSnapshotAccountsSaga),
    takeLatest(deleteCreditLines.request, requestDeleteCreditLineSaga),
    takeLatest(updateSnapshotAccount.request, requestUpdateSnapshotAccountSaga),
    takeLatest(updateSnapshotAccounts.request, requestUpdateSnapshotAccountsSaga),
    takeLatest(archiveClient.request, requestArchiveClientSaga),
    takeLatest(deleteClient.request, requestDeleteClientSaga),
  ]);
}
